import { ButtonType, EpicIcons, TooltipPosition } from "@constellation-academy/epic-ui";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import { useRef, useState } from "react";
import { useFragment, useMutation } from "react-relay";
import { useNavigate } from "react-router-dom";
import { Button, ButtonVariant } from "@components/button";
import { useTranslation } from "@i18n/hooks";
import { type usersInAccountTable_RemoveUserFromAccountMutation } from "@relay/usersInAccountTable_RemoveUserFromAccountMutation.graphql";
import { type usersInAccountTable_UserInAccountConnectionFragment$key } from "@relay/usersInAccountTable_UserInAccountConnectionFragment.graphql";
import { Paths } from "@routes/paths";
import { USER_GROUP_NAME } from "./users-in-account-table.consts";
import {
	REMOVE_USER_FROM_ACCOUNT_MUTATION,
	USER_IN_ACCOUNT_CONNECTION_FRAGMENT,
} from "./users-in-account-table.graphql";
import {
	type EditUserInAccountGroups,
	type UsersInAccountTableProps,
} from "./users-in-account-table.interface";
import { convertUserInAccountGroupName } from "./users-in-account-table.util";
import { DataTable } from "../../../components/data-table";
import { EditUserInAccountGroup } from "../edit-user-in-account-group/edit-user-in-account-group.component";

export const UsersInAccountTable = ({
	accountId,
	connections,
	hasNext,
	loadNext,
	userInAccountConnectionRef,
}: UsersInAccountTableProps) => {
	const { t } = useTranslation("common");
	const toastRef = useRef<Toast>(null);
	const navigate = useNavigate();

	const { edges: users } = useFragment<usersInAccountTable_UserInAccountConnectionFragment$key>(
		USER_IN_ACCOUNT_CONNECTION_FRAGMENT,
		userInAccountConnectionRef,
	);

	const [removeUserFromAccount] = useMutation<usersInAccountTable_RemoveUserFromAccountMutation>(
		REMOVE_USER_FROM_ACCOUNT_MUTATION,
	);

	const [editUserInAccountGroups, setEditUserInAccountGroups] = useState<
		EditUserInAccountGroups | undefined
	>();

	const handleViewUserOnClick = (userId: string) => {
		navigate(Paths.users.withId(userId).edit.path);
	};

	const handleEditUserOnClick = (
		userId: string,
		groups: ReadonlyArray<{ readonly id: string; readonly name: string }>,
	) => {
		const groupsId = groups?.filter((g) => g.name !== USER_GROUP_NAME).map((g) => g.id);
		setEditUserInAccountGroups({ userId, groupsId });
	};

	const handleUserInAccountGroupDialogOnHide = () => {
		setEditUserInAccountGroups(undefined);
	};

	const handleDeleteUserOnClick = (userId: string) => {
		confirmDialog({
			message: t("user.removeFromAccount"),
			header: t("user.removeUser"),
			icon: "pi pi-info-circle",
			acceptClassName: "p-button-danger",
			acceptLabel: t("actions.yes"),
			rejectLabel: t("actions.no"),
			accept: () =>
				removeUserFromAccount({
					variables: {
						input: {
							userId,
							accountId,
						},
						connections: connections ?? [],
					},
				}),
		});
	};

	return (
		<div className="mb-5">
			<DataTable
				emptyMessage={
					<div className="flex justify-content-center align-items-center">
						<div className="mr-2">{t("user.noUsersFound")}</div>
					</div>
				}
				className="mb-3"
				value={users?.map((edge) => edge!.node)}
			>
				{(Column) => (
					<>
						<Column header={t("table.name")} field="user.name" />
						<Column header={t("table.email")} field="user.email" />
						<Column
							header={t("table.activated")}
							body={(row) => {
								return row?.user?.activated
									? t("user.activated")
									: t("user.notActivated");
							}}
						/>
						<Column
							header={t("table.permissions")}
							body={(row) => {
								return row?.groups?.map((groupAssociation: any) => {
									return (
										<div key={groupAssociation.id} className="flex">
											<div>
												{convertUserInAccountGroupName(
													groupAssociation.name,
												)}
											</div>
										</div>
									);
								});
							}}
						/>
						<Column
							header={t("table.actions")}
							style={{ width: "5%" }}
							body={(row) => {
								return (
									<div className="flex gap-2">
										<Button
											onClick={() => {
												handleViewUserOnClick(row.user.id);
											}}
											icon={EpicIcons.EYE}
											tooltip={t("user.viewUser")}
											position={TooltipPosition.Top}
										/>
										<Button
											onClick={() => {
												handleEditUserOnClick(row.user.id, row.groups);
											}}
											icon={EpicIcons.PENCIL}
											tooltip={t("account.editGroups")}
											position={TooltipPosition.Top}
										/>
										<Button
											onClick={() => {
												handleDeleteUserOnClick(row.user.id);
											}}
											icon={EpicIcons.TRASH}
											variant={ButtonVariant.Error}
											tooltip={t("user.deleteUser")}
											position={TooltipPosition.Top}
										/>
									</div>
								);
							}}
						/>
					</>
				)}
			</DataTable>
			{hasNext && (
				<div className="flex justify-content-center align-items-center">
					<Button
						type={ButtonType.Button}
						variant={ButtonVariant.Strong}
						disabled={!hasNext}
						onClick={loadNext}
						label={t("actions.loadMore")}
					/>
				</div>
			)}
			<Dialog
				visible={!!editUserInAccountGroups}
				onHide={handleUserInAccountGroupDialogOnHide}
				className="w-25rem"
				header={t("account.editGroups")}
			>
				{editUserInAccountGroups && (
					<EditUserInAccountGroup
						accountId={accountId!}
						userId={editUserInAccountGroups.userId}
						groupsId={editUserInAccountGroups.groupsId}
						onUserEdited={handleUserInAccountGroupDialogOnHide}
					/>
				)}
			</Dialog>
			<Toast ref={toastRef} />
			<ConfirmDialog />
		</div>
	);
};
